import React, { useMemo, useState } from "react";
import { useCart } from "../context/CartContext";
import { SITE, createWhatsAppLink } from "../config/site";

/* utils */
const moneyKES = (n) => `KSh ${Number(n || 0).toLocaleString()}`;
const uid = () => "ORD-" + Math.random().toString(36).slice(2, 8).toUpperCase();

/* very small local “orders api” so it works without backend */
const Orders = {
  all() {
    try { return JSON.parse(localStorage.getItem("orders:v1") || "[]"); } catch { return []; }
  },
  save(list) {
    try { localStorage.setItem("orders:v1", JSON.stringify(list)); } catch {}
  },
  create(order) {
    const list = Orders.all();
    list.unshift(order);
    Orders.save(list);
    return order;
  }
};

export default function Checkout() {
  const { items, total, clear, subtotal, discountAmount } = useCart();

  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    email: "",
    address: "",
    payment: "cod", // cod | mpesa | bank | whatsapp
    note: "",
  });
  const [placed, setPlaced] = useState(null); // <- created order

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handlePlaceOrder = (e) => {
    e.preventDefault();
    if (!items?.length) return;

    const orderId = uid();
    const order = {
      id: orderId,
      createdAt: new Date().toISOString(),
      customer: {
        fullName: form.fullName, phone: form.phone, email: form.email, address: form.address,
      },
      items,
      subtotal,
      discountAmount,
      total,
      note: form.note,
      payment: form.payment,
      status: form.payment === "cod" ? "confirmed_cod" : "awaiting_payment",
    };

    Orders.create(order);
    setPlaced(order);

    // Clear the cart after placing the order
    clear();
  };

  const waText = useMemo(() => {
    if (!placed) return "";
    const lines = [
      `Hello, I'd like to confirm my order ${placed.id}.`,
      `Name: ${placed.customer.fullName}`,
      `Phone: ${placed.customer.phone}`,
      `Total: ${moneyKES(placed.total)}`,
      `Payment: ${placed.payment.toUpperCase()}`,
      "",
      "Items:",
      ...placed.items.map((it) => `- ${it.name}${it.size ? ` (Size ${it.size})` : ""} × ${it.qty}`),
    ];
    return encodeURIComponent(lines.join("\n"));
  }, [placed]);

  if (!items?.length && !placed) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="text-xl font-semibold">Checkout</h1>
        <p className="text-slate-600 mt-2">Your cart is empty.</p>
      </main>
    );
  }

  // Success / Instructions screen
  if (placed) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-10 space-y-6">
        <h1 className="text-xl font-semibold">Order Placed</h1>
        <div className="rounded-2xl border p-4 bg-white">
          <div className="text-sm">
            <div><span className="font-semibold">Order ID:</span> {placed.id}</div>
            <div><span className="font-semibold">Total:</span> {moneyKES(placed.total)}</div>
            <div><span className="font-semibold">Status:</span> {placed.status.replace("_", " ")}</div>
          </div>

          {/* Payment instructions */}
          <div className="mt-4 space-y-3">
            {placed.payment === "cod" && (
              <div className="text-sm">
                <div className="font-semibold">Cash on Delivery</div>
                <p>Pay in cash upon delivery. Our agent will call {placed.customer.phone} to schedule delivery.</p>
              </div>
            )}

            {placed.payment === "mpesa" && (
              <div className="text-sm">
                <div className="font-semibold">M-Pesa Manual Payment</div>
                <ul className="list-disc ml-5 mt-1 space-y-1">
                  <li>Go to <strong>Lipa na M-Pesa</strong> → <strong>Buy Goods and Services</strong></li>
                  <li><strong>Till Number:</strong> <code>123456</code> (replace with your till)</li>
                  <li><strong>Amount:</strong> {moneyKES(placed.total)}</li>
                  <li>Reference: <strong>{placed.id}</strong></li>
                </ul>
                <p className="mt-2">Send the confirmation SMS/receipt to our WhatsApp below or reply to your order email.</p>
              </div>
            )}

            {placed.payment === "bank" && (
              <div className="text-sm">
                <div className="font-semibold">Bank Transfer</div>
                <ul className="list-disc ml-5 mt-1 space-y-1">
                  <li><strong>Bank:</strong> Your Bank Name</li>
                  <li><strong>Account Name:</strong> Your Company Ltd</li>
                  <li><strong>Account No:</strong> 00123456789</li>
                  <li><strong>Branch:</strong> Nairobi</li>
                  <li><strong>Amount:</strong> {moneyKES(placed.total)} | Ref: <strong>{placed.id}</strong></li>
                </ul>
                <p className="mt-2">Upload/forward the transfer slip via WhatsApp link below.</p>
              </div>
            )}

            {placed.payment === "whatsapp" && (
              <div className="text-sm">
                <div className="font-semibold">Pay / Confirm on WhatsApp</div>
                <p>Tap the WhatsApp button to send us your order details and arrange payment (M-Pesa / cash / bank).</p>
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center gap-2">
            <a
              href={createWhatsAppLink(decodeURIComponent(waText))}
              target="_blank"
              rel="noreferrer"
              className="rounded-xl bg-emerald-600 text-white px-4 py-2 text-sm"
            >
              Message us on WhatsApp
            </a>
            <a
              href="/"
              className="rounded-xl border px-4 py-2 text-sm"
            >
              Continue Shopping
            </a>
          </div>
        </div>
      </main>
    );
  }

  // Form screen
  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <h1 className="text-xl font-semibold">Checkout</h1>

      <form onSubmit={handlePlaceOrder} className="mt-6 grid gap-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-600 mb-1">Full Name</label>
            <input
              required
              name="fullName"
              value={form.fullName}
              onChange={onChange}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              placeholder="John Doe"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">Phone</label>
            <input
              required
              name="phone"
              value={form.phone}
              onChange={onChange}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              placeholder="+2547..."
            />
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">Email (optional)</label>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              placeholder="you@example.com"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-slate-600 mb-1">Delivery Address</label>
            <input
              required
              name="address"
              value={form.address}
              onChange={onChange}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              placeholder="Street, estate, city"
            />
          </div>
        </div>

        <div>
          <div className="text-sm font-semibold mb-2">Payment Method</div>
          <div className="grid sm:grid-cols-2 gap-2">
            <label className="flex items-center gap-2 rounded-lg border p-2">
              <input
                type="radio"
                name="payment"
                value="cod"
                checked={form.payment === "cod"}
                onChange={onChange}
              />
              <span className="text-sm">Cash on Delivery</span>
            </label>
            <label className="flex items-center gap-2 rounded-lg border p-2">
              <input
                type="radio"
                name="payment"
                value="mpesa"
                checked={form.payment === "mpesa"}
                onChange={onChange}
              />
              <span className="text-sm">M-Pesa (manual till/paybill)</span>
            </label>
            <label className="flex items-center gap-2 rounded-lg border p-2">
              <input
                type="radio"
                name="payment"
                value="bank"
                checked={form.payment === "bank"}
                onChange={onChange}
              />
              <span className="text-sm">Bank Transfer</span>
            </label>
            <label className="flex items-center gap-2 rounded-lg border p-2">
              <input
                type="radio"
                name="payment"
                value="whatsapp"
                checked={form.payment === "whatsapp"}
                onChange={onChange}
              />
              <span className="text-sm">Confirm & Pay on WhatsApp</span>
            </label>
          </div>
        </div>

        <div className="rounded-2xl border border-orange-100 bg-orange-50 p-4 text-sm text-slate-700">
          Need help before you pay? Contact us on <a href={SITE.phoneHref} className="font-semibold text-[#e46a2e]">{SITE.phoneDisplay}</a> or visit us at <span className="font-semibold">{SITE.locationTitle}, {SITE.locationLine}</span>.
        </div>

        <div>
          <label className="block text-xs text-slate-600 mb-1">Notes (optional)</label>
          <textarea
            name="note"
            value={form.note}
            onChange={onChange}
            rows={3}
            className="w-full rounded-lg border px-3 py-2 text-sm"
            placeholder="Landmark, delivery instructions, etc."
          />
        </div>

        {/* order summary */}
        <div className="rounded-2xl border p-4 bg-white">
          <div className="text-sm font-semibold mb-2">Order Summary</div>
          <ul className="text-sm space-y-1">
            {items.map((it) => (
              <li key={it.key ?? `${it.id}-${it.size}`}>
                {it.name} {it.size ? `(Size ${it.size})` : ""} × {it.qty}
              </li>
            ))}
          </ul>
          <div className="mt-3 text-sm flex items-center justify-between">
            <span className="text-slate-600">Subtotal</span>
            <span className="font-medium">{moneyKES(subtotal)}</span>
          </div>
          <div className="text-sm flex items-center justify-between">
            <span className="text-slate-600">Discount</span>
            <span className="font-medium">- {moneyKES(discountAmount || 0)}</span>
          </div>
          <div className="mt-1 pt-2 border-t flex items-center justify-between">
            <span className="text-sm font-semibold">Total</span>
            <span className="text-sm font-bold">{moneyKES(total)}</span>
          </div>
        </div>

        <button
          type="submit"
          className="w-full rounded-xl bg-slate-900 text-white py-2 text-sm font-semibold"
        >
          Place Order
        </button>
      </form>
    </main>
  );
}
